import { createSelector } from "@reduxjs/toolkit";
import { compact } from "lodash";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import withShouldRender from "modules/Core/higher-order-components/withShouldRender";
import useOnError from "modules/Core/sub-modules/Dialog/hooks/useOnError";
import useOnSuccess from "modules/Core/sub-modules/Dialog/hooks/useOnSuccess";
import { Button } from "modules/Core/sub-modules/ui-kit/exports";
import cells from "modules/cycles/state/index";
import styles from "./styles.module.scss";

const makeIsReadySelector = createSelector(
    [
        (state) => state?.cycles?.entities,
        (state, cycleId) => cycleId
    ],
    (cycles, cycleId) => {
        const cycle = cycles?.[cycleId];
        const flow = compact(cycle?.flow);
        if (!flow.length) return false;
        return flow.every((activity) => Boolean(activity?.stamps?.completed));
    }
)

export default strappedConnected(
    withShouldRender(Button),
    {
        activeCycle: cells.fetchCurrentCycle.selectors.activeCycle,
    },
    {
        completeCycle: cells.completeCycle.action,
        refetchActive: (boothId) => cells.fetchCurrentCycle.action({ boothId }),
    },
    ({
        cycleId,
        completeCycle,
        callback,
        text = "Complete Cycle",
        activeCycle,
        boothId,
        refetchActive,
        panel,
        className,
    }) => {
        const resolvedCycleId = cycleId ?? activeCycle?.id
        const resolvedBoothId = boothId ?? activeCycle?.boothId
        const isReady = useSelector(state => makeIsReadySelector(state, resolvedCycleId));
        const success = useOnSuccess();
        const error = useOnError();
        const onSuccess = useCallback((res, close) => {
            if (!res) return error("Failed to complete cycle");
            callback?.(res)
            if (!callback && resolvedBoothId) refetchActive(resolvedBoothId)
            success("Cycle Completed!")
            close()
        }, [callback, success, error, refetchActive, resolvedBoothId]);

        const complete = useCallback(
            (close) => completeCycle({ id: resolvedCycleId, callback: (res) => onSuccess(res, close) }),

            [resolvedCycleId, onSuccess, completeCycle]
        );

        return {
            text,
            onClick: complete,
            modal: !isReady,
            onConfirm: complete,
            copy: {
                description: "Not all steps have been completed, are you sure you want to complete this cycle?",
                title: "Complete Cycle?",
            },
            shouldRender: Boolean(resolvedCycleId),
            panel,
            noChevron: panel,
            span: panel,
            maxWidth: panel,
            className: panel ? styles.panelButton : className,
        }
    }
)
